import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import EquityCurve from './EquityCurve';

export default function Analytics() {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTrades = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('trades')
      .select('*')
      .eq('user_id', user.id)
      .order('trade_date', { ascending: true });
    setTrades(data || []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const stats = useMemo(() => {
    if (trades.length === 0) return null;

    const wins = trades.filter(t => t.outcome === 'Win');
    const losses = trades.filter(t => t.outcome === 'Loss');
    const scratches = trades.filter(t => t.outcome === 'Scratch');

    const winRate = (wins.length / trades.length) * 100;
    const winRs = wins.map(t => parseFloat(t.r_multiple) || 0);
    const lossRs = losses.map(t => Math.abs(parseFloat(t.r_multiple) || 0));

    const avgWin = winRs.length ? winRs.reduce((a,b) => a+b, 0) / winRs.length : 0;
    const avgLoss = lossRs.length ? lossRs.reduce((a,b) => a+b, 0) / lossRs.length : 0;
    const profitFactor = avgLoss > 0 ? (avgWin * wins.length) / (avgLoss * losses.length) : 0;

    const totalR = trades.reduce((sum, t) => sum + (parseFloat(t.r_multiple) || 0), 0);
    const expectancy = totalR / trades.length;

    // Best/worst trades — only count trades with valid R-multiple
    const tradesWithR = trades.filter(t => t.r_multiple !== null && !isNaN(parseFloat(t.r_multiple)));
    const sorted = [...tradesWithR].sort((a, b) => (parseFloat(b.r_multiple) || 0) - (parseFloat(a.r_multiple) || 0));
    const positiveRs = sorted.filter(t => parseFloat(t.r_multiple) > 0);
    const negativeRs = sorted.filter(t => parseFloat(t.r_multiple) < 0);
    const best = positiveRs.length > 0 ? positiveRs[0] : null;
    const worst = negativeRs.length > 0 ? negativeRs[negativeRs.length - 1] : null;

    // Streaks
    let currentStreak = 0, longestWin = 0, longestLoss = 0, tmpW = 0, tmpL = 0;
    const sortedByDate = [...trades].sort((a, b) => new Date(a.trade_date) - new Date(b.trade_date));
    sortedByDate.forEach(t => {
      if (t.outcome === 'Win') { tmpW++; tmpL = 0; if (tmpW > longestWin) longestWin = tmpW; }
      else if (t.outcome === 'Loss') { tmpL++; tmpW = 0; if (tmpL > longestLoss) longestLoss = tmpL; }
      else { tmpW = 0; tmpL = 0; }
    });
    const last = sortedByDate[sortedByDate.length - 1];
    if (last) {
      let count = 0;
      for (let i = sortedByDate.length - 1; i >= 0; i--) {
        if (sortedByDate[i].outcome === last.outcome) count++;
        else break;
      }
      currentStreak = count;
    }

    // By session
    const sessions = {};
    trades.forEach(t => {
      const s = t.session || 'Unknown';
      if (!sessions[s]) sessions[s] = { total:0, wins:0, totalR:0 };
      sessions[s].total++;
      if (t.outcome === 'Win') sessions[s].wins++;
      sessions[s].totalR += parseFloat(t.r_multiple) || 0;
    });

    // By setup
    const setups = {};
    trades.forEach(t => {
      const s = t.setup_type || 'Unspecified';
      if (!setups[s]) setups[s] = { total:0, wins:0, totalR:0 };
      setups[s].total++;
      if (t.outcome === 'Win') setups[s].wins++;
      setups[s].totalR += parseFloat(t.r_multiple) || 0;
    });

    // By day of week
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const byDay = days.map(d => ({ day: d, total: 0, wins: 0, totalR: 0 }));
    trades.forEach(t => {
      const dow = new Date(t.trade_date).getDay();
      byDay[dow].total++;
      if (t.outcome === 'Win') byDay[dow].wins++;
      byDay[dow].totalR += parseFloat(t.r_multiple) || 0;
    });

    // By direction
    const longs = trades.filter(t => t.direction === 'Long');
    const shorts = trades.filter(t => t.direction === 'Short');
    const longWinRate = longs.length ? (longs.filter(t => t.outcome === 'Win').length / longs.length) * 100 : 0;
    const shortWinRate = shorts.length ? (shorts.filter(t => t.outcome === 'Win').length / shorts.length) * 100 : 0;

    // Checklist correlation
    const fullChecklist = trades.filter(t => t.checklist_complete === 'Yes');
    const noChecklist = trades.filter(t => t.checklist_complete === 'No');
    const partialChecklist = trades.filter(t => t.checklist_complete === 'Partial');
    const fullWR = fullChecklist.length ? (fullChecklist.filter(t => t.outcome === 'Win').length / fullChecklist.length * 100) : 0;
    const noWR = noChecklist.length ? (noChecklist.filter(t => t.outcome === 'Win').length / noChecklist.length * 100) : 0;
    const partialWR = partialChecklist.length ? (partialChecklist.filter(t => t.outcome === 'Win').length / partialChecklist.length * 100) : 0;

    return {
      total: trades.length, winRate, avgWin, avgLoss, profitFactor, expectancy, totalR,
      best, worst, longestWin, longestLoss, currentStreak, lastOutcome: last?.outcome,
      sessions, setups, byDay, longs, shorts, longWinRate, shortWinRate,
      fullChecklist, noChecklist, partialChecklist, fullWR, noWR, partialWR,
      winsCount: wins.length, lossesCount: losses.length, scratchesCount: scratches.length
    };
  }, [trades]);

  if (loading) return <div style={{textAlign:'center',padding:32,color:'var(--text-3)'}}>Loading analytics...</div>;

  if (!stats || trades.length === 0) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Performance <em>Analytics</em></h1>
          <p className="page-subtitle">Deep stats on your trading performance</p>
        </div>
        <div className="card" style={{textAlign:'center',padding:48}}>
          <div style={{fontSize:40,marginBottom:12}}>📊</div>
          <p style={{color:'var(--text-3)'}}>Log some trades in the Journal to unlock your analytics.</p>
        </div>
      </div>
    );
  }

  function BarRow({ label, total, wins, totalR, maxTotal }) {
    const winPct = total ? (wins / total) * 100 : 0;
    const widthPct = (total / maxTotal) * 100;
    return (
      <div className="analytic-row">
        <div className="analytic-row-label">{label}</div>
        <div style={{flex:1, position:'relative'}}>
          <div style={{height:8, background:'var(--bg-5)', borderRadius:4, overflow:'hidden'}}>
            <div style={{
              width: `${widthPct}%`,
              height: '100%',
              background: winPct >= 50 ? 'linear-gradient(90deg, var(--green), rgba(62,207,142,0.5))' : 'linear-gradient(90deg, var(--red), rgba(224,82,82,0.5))'
            }}></div>
          </div>
        </div>
        <div className="analytic-row-stats">
          <span>{total} trades</span>
          <span style={{color: winPct >= 50 ? 'var(--green)' : 'var(--red)'}}>{winPct.toFixed(0)}% WR</span>
          <span style={{color: totalR >= 0 ? 'var(--green)' : 'var(--red)'}}>{totalR > 0 ? '+' : ''}{totalR.toFixed(2)}R</span>
        </div>
      </div>
    );
  }

  const maxSession = Math.max(...Object.values(stats.sessions).map(s => s.total));
  const maxSetup = Math.max(...Object.values(stats.setups).map(s => s.total));
  const maxDay = Math.max(...stats.byDay.map(d => d.total));

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Performance <em>Analytics</em></h1>
        <p className="page-subtitle">Deep performance breakdown across {stats.total} trades</p>
      </div>

      {/* TOP STATS */}
      <div className="kpi-grid">
        <div className="kpi-cell">
          <div className="kpi-label">TOTAL TRADES</div>
          <div className="kpi-value">{stats.total}</div>
          <div className="kpi-note">{stats.winsCount}W / {stats.lossesCount}L / {stats.scratchesCount}S</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">WIN RATE</div>
          <div className="kpi-value" style={{color: stats.winRate >= 50 ? 'var(--green)' : 'var(--amber)'}}>{stats.winRate.toFixed(1)}%</div>
          <div className="kpi-note">target: 47–53%</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">PROFIT FACTOR</div>
          <div className="kpi-value" style={{color: stats.profitFactor >= 1.5 ? 'var(--green)' : stats.profitFactor >= 1 ? 'var(--amber)' : 'var(--red)'}}>
            {stats.profitFactor.toFixed(2)}
          </div>
          <div className="kpi-note">{stats.profitFactor >= 2 ? 'excellent' : stats.profitFactor >= 1.5 ? 'solid' : stats.profitFactor >= 1 ? 'profitable' : 'losing'}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">EXPECTANCY</div>
          <div className="kpi-value" style={{color: stats.expectancy >= 0 ? 'var(--green)' : 'var(--red)'}}>
            {stats.expectancy > 0 ? '+' : ''}{stats.expectancy.toFixed(2)}R
          </div>
          <div className="kpi-note">per trade avg</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">TOTAL R</div>
          <div className="kpi-value" style={{color: stats.totalR >= 0 ? 'var(--green)' : 'var(--red)'}}>
            {stats.totalR > 0 ? '+' : ''}{stats.totalR.toFixed(2)}R
          </div>
          <div className="kpi-note">cumulative</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">AVG WIN / LOSS</div>
          <div className="kpi-value">{stats.avgWin.toFixed(2)} / {stats.avgLoss.toFixed(2)}</div>
          <div className="kpi-note">R ratio</div>
        </div>
      </div>

      {/* EQUITY CURVE */}
      <div className="card mb-24">
        <div className="card-title">EQUITY CURVE</div>
        <EquityCurve trades={trades} height={260} />
      </div>

      {/* STREAKS */}
      <div className="card mb-24">
        <div className="card-title">STREAK ANALYSIS</div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12}}>
          <div className="output-row">
            <span className="output-label">Longest Win Streak</span>
            <span className="output-value green">{stats.longestWin}</span>
          </div>
          <div className="output-row">
            <span className="output-label">Longest Loss Streak</span>
            <span className="output-value red">{stats.longestLoss}</span>
          </div>
          <div className="output-row">
            <span className="output-label">Current Streak</span>
            <span className={`output-value ${stats.lastOutcome === 'Win' ? 'green' : stats.lastOutcome === 'Loss' ? 'red' : ''}`}>
              {stats.currentStreak} {stats.lastOutcome === 'Win' ? 'W' : stats.lastOutcome === 'Loss' ? 'L' : 'S'}
            </span>
          </div>
        </div>
      </div>

      {/* BY SESSION */}
      <div className="card mb-24">
        <div className="card-title">PERFORMANCE BY SESSION</div>
        <div className="analytic-list">
          {Object.entries(stats.sessions)
            .sort((a, b) => b[1].totalR - a[1].totalR)
            .map(([name, s]) => (
              <BarRow key={name} label={name} total={s.total} wins={s.wins} totalR={s.totalR} maxTotal={maxSession} />
            ))}
        </div>
      </div>

      {/* BY SETUP */}
      <div className="card mb-24">
        <div className="card-title">PERFORMANCE BY SETUP TYPE</div>
        <div className="analytic-list">
          {Object.entries(stats.setups)
            .sort((a, b) => b[1].totalR - a[1].totalR)
            .slice(0, 8)
            .map(([name, s]) => (
              <BarRow key={name} label={name.length > 30 ? name.slice(0,28)+'...' : name} total={s.total} wins={s.wins} totalR={s.totalR} maxTotal={maxSetup} />
            ))}
        </div>
      </div>

      {/* BY DAY OF WEEK */}
      <div className="card mb-24">
        <div className="card-title">PERFORMANCE BY DAY OF WEEK</div>
        <div className="analytic-list">
          {stats.byDay.filter(d => d.total > 0).map(d => (
            <BarRow key={d.day} label={d.day} total={d.total} wins={d.wins} totalR={d.totalR} maxTotal={maxDay} />
          ))}
        </div>
      </div>

      <div className="section-row">
        {/* DIRECTION */}
        <div className="card">
          <div className="card-title">LONG vs SHORT</div>
          <div className="output-row">
            <span className="output-label">Long Trades</span>
            <span className="output-value">{stats.longs.length} ({stats.longWinRate.toFixed(0)}% WR)</span>
          </div>
          <div className="output-row mt-8">
            <span className="output-label">Short Trades</span>
            <span className="output-value">{stats.shorts.length} ({stats.shortWinRate.toFixed(0)}% WR)</span>
          </div>
        </div>

        {/* CHECKLIST */}
        <div className="card">
          <div className="card-title">CHECKLIST DISCIPLINE</div>
          <div className="output-row">
            <span className="output-label">All 12 Confirmed</span>
            <span className="output-value green">{stats.fullChecklist.length} trades · {stats.fullWR.toFixed(0)}% WR</span>
          </div>
          <div className="output-row mt-8">
            <span className="output-label">Partial</span>
            <span className="output-value amber">{stats.partialChecklist.length} trades · {stats.partialWR.toFixed(0)}% WR</span>
          </div>
          <div className="output-row mt-8">
            <span className="output-label">Skipped Checklist</span>
            <span className="output-value red">{stats.noChecklist.length} trades · {stats.noWR.toFixed(0)}% WR</span>
          </div>
          {stats.fullChecklist.length > 0 && stats.noChecklist.length > 0 && (
            <p style={{fontSize:12, color:'var(--text-3)', marginTop:12, fontStyle:'italic'}}>
              Your win rate is <strong style={{color:'var(--gold-2)'}}>{(stats.fullWR - stats.noWR).toFixed(0)}% higher</strong> when you complete the full checklist.
            </p>
          )}
        </div>
      </div>

      {/* BEST & WORST */}
      <div className="section-row mt-24">
        <div className="card">
          <div className="card-title">🏆 BEST TRADE</div>
          {stats.best ? (
            <div style={{fontSize:13, color:'var(--text-2)', lineHeight:1.8}}>
              <div><strong style={{color:'var(--green)'}}>+{parseFloat(stats.best.r_multiple).toFixed(2)}R</strong> on {stats.best.trade_date}</div>
              <div>{stats.best.direction} · {stats.best.session || 'No session'}</div>
              <div style={{color:'var(--text-3)', fontSize:12, marginTop:6}}>{stats.best.setup_type || 'No setup tagged'}</div>
            </div>
          ) : (
            <div style={{fontSize:13, color:'var(--text-4)', fontStyle:'italic'}}>
              No winning trades yet. Log your first winner to see it here.
            </div>
          )}
        </div>
        <div className="card">
          <div className="card-title">📉 WORST TRADE</div>
          {stats.worst ? (
            <div style={{fontSize:13, color:'var(--text-2)', lineHeight:1.8}}>
              <div><strong style={{color:'var(--red)'}}>{parseFloat(stats.worst.r_multiple).toFixed(2)}R</strong> on {stats.worst.trade_date}</div>
              <div>{stats.worst.direction} · {stats.worst.session || 'No session'}</div>
              <div style={{color:'var(--text-3)', fontSize:12, marginTop:6}}>{stats.worst.setup_type || 'No setup tagged'}</div>
            </div>
          ) : (
            <div style={{fontSize:13, color:'var(--text-4)', fontStyle:'italic'}}>
              No losing trades logged. Keep it that way.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
